/**
 * @file Helper functions for composeMetaText container
 */

import { EditorUsage } from '../../../components/Common/Editor/types';
import translate from '../../../i18n/translate';
import { ComposeNodeType } from '../props';

/**
 * Map compose node type to editor usage for large screens,
 * so that correct controls (and styles) are shown
 */
const usageMappingDesktop: Record<ComposeNodeType, EditorUsage> = {
  MESSAGE: 'message',
  TEMPLATE: 'template',
};

/**
 * Map compose node type to editor usage for small screens,
 * so that correct controls (and styles) are shown
 */
const usageMappingMobile: Record<ComposeNodeType, EditorUsage> = {
  MESSAGE: 'messageEdit',
  TEMPLATE: 'templateMobile',
};

/**
 * Get value for the Editor usage prop
 *
 * @param isMobile Whether the user is on a small screen
 * @param nodeType Whether the compose form is being used for messages or templates
 * @returns        The Editor usage to use
 */
export const getUsage = (isMobile: boolean, nodeType: ComposeNodeType) => {
  const mapper = isMobile ? usageMappingMobile : usageMappingDesktop;
  return mapper[nodeType];
};

/**
 * Get error and helper text for the editor
 *
 * @param shouldCheckRequired Whether to check if input is required
 * @param value               The value of the input
 * @param maxLength           Max length of the input
 *
 * @returns                   Object with error(boolean) and helperText(string) properties
 */
export const getValidityProps = (
  shouldCheckRequired: boolean,
  value: HTMLInputElement['value'],
  maxLength: HTMLInputElement['maxLength'],
): {
  error: boolean;
  helperText: string;
} => {
  if (shouldCheckRequired && value.trim().length === 0) {
    return {
      error: true,
      helperText: translate('GENERAL__FILL_FIELD'),
    };
  }

  if (value.length > maxLength) {
    return {
      error: true,
      helperText: translate('MESSAGE__ERROR__TEXT_SIZE'),
    };
  }

  return { error: false, helperText: '' };
};
